import { useEffect, useState, useCallback } from "react";
import { Search, Phone, Mail, MessageCircle, Loader2, Inbox } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { listEnquiries, updateEnquiry } from "@/lib/adminApi";
import { whatsappLink } from "@/lib/whatsapp";

const STATUSES = ["NEW", "CONTACTED", "FOLLOW_UP", "CONVERTED", "CLOSED"];
const STATUS_COLORS = {
  NEW: { bg: "rgba(8,127,191,0.12)", c: "#087FBF" },
  CONTACTED: { bg: "rgba(22,184,212,0.14)", c: "#0e8ba3" },
  FOLLOW_UP: { bg: "rgba(224,168,0,0.14)", c: "#8a6400" },
  CONVERTED: { bg: "rgba(63,174,53,0.14)", c: "#197A35" },
  CLOSED: { bg: "rgba(100,116,139,0.14)", c: "#475569" },
};

function NotesDialog({ enquiry, onSave }) {
  const [notes, setNotes] = useState(enquiry.notes || "");
  const [saving, setSaving] = useState(false);
  return (
    <Dialog>
      <DialogTrigger asChild>
        <button className="text-xs font-semibold underline" style={{ color: "#087FBF" }} data-testid={`notes-${enquiry.id}`}>Notes</button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>Notes — {enquiry.customer_name}</DialogTitle></DialogHeader>
        <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={5} placeholder="Add internal notes..." />
        <button className="btn-primary" disabled={saving} onClick={async () => { setSaving(true); try { await onSave(enquiry.id, { notes }); toast.success("Notes saved"); } catch { toast.error("Failed"); } finally { setSaving(false); } }}>
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Save Notes
        </button>
      </DialogContent>
    </Dialog>
  );
}

export default function AdminEnquiries() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("ALL");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const PAGE_SIZE = 20;

  const load = useCallback(() => {
    setLoading(true);
    const params = { skip: page * PAGE_SIZE, limit: PAGE_SIZE };
    if (status !== "ALL") params.status = status;
    if (search.trim()) params.search = search.trim();
    listEnquiries(params).then(setRows).catch(() => toast.error("Failed to load")).finally(() => setLoading(false));
  }, [status, search, page]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  const changeStatus = async (id, newStatus) => {
    try {
      await updateEnquiry(id, { status: newStatus });
      setRows((prev) => prev.map((r) => (r.id === id ? { ...r, status: newStatus } : r)));
      toast.success("Status updated");
    } catch {
      toast.error("Update failed");
    }
  };

  const saveNotes = async (id, payload) => {
    const updated = await updateEnquiry(id, payload);
    setRows((prev) => prev.map((r) => (r.id === id ? updated : r)));
  };

  return (
    <div>
      <h1 className="font-heading text-3xl font-extrabold" style={{ color: "#063B63" }}>Enquiries</h1>
      <p className="text-sm mt-1" style={{ color: "#4A6B82" }}>View and manage customer enquiries. Email delivery is not enabled — records are stored here.</p>

      <div className="mt-6 flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2" style={{ color: "#94a3b8" }} />
          <Input data-testid="enq-search" value={search} onChange={(e) => { setSearch(e.target.value); setPage(0); }} placeholder="Search name, mobile, email or enquiry ID..." className="pl-9" />
        </div>
        <Select value={status} onValueChange={(v) => { setStatus(v); setPage(0); }}>
          <SelectTrigger data-testid="enq-status-filter" className="sm:w-48"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="ALL">All Statuses</SelectItem>
            {STATUSES.map((s) => <SelectItem key={s} value={s}>{s.replace("_", " ")}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="py-20 flex justify-center"><Loader2 className="w-8 h-8 animate-spin" style={{ color: "#087FBF" }} /></div>
      ) : rows.length === 0 ? (
        <div className="py-20 text-center"><Inbox className="w-12 h-12 mx-auto mb-3" style={{ color: "#16B8D4" }} /><p className="text-sm" style={{ color: "#4A6B82" }}>No enquiries found.</p></div>
      ) : (
        <div className="mt-5 space-y-3">
          {rows.map((r) => {
            const sc = STATUS_COLORS[r.status] || STATUS_COLORS.NEW;
            return (
              <div key={r.id} data-testid={`enq-row-${r.id}`} className="biosyn-card p-4">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-heading font-bold" style={{ color: "#063B63" }}>{r.customer_name}</p>
                      <span className="text-xs font-mono" style={{ color: "#087FBF" }}>{r.enquiry_number}</span>
                      <span className="text-[10px] font-bold px-2 py-0.5 rounded-full" style={{ background: sc.bg, color: sc.c }}>{r.status.replace("_", " ")}</span>
                    </div>
                    <p className="text-xs mt-1" style={{ color: "#4A6B82" }}>
                      {new Date(r.created_at).toLocaleString()} • {r.product_name || "General"} • {r.farm_type || "—"} • {r.species || "—"} • {r.location || "—"}
                    </p>
                    {r.message && <p className="text-sm mt-2" style={{ color: "#17324D" }}>{r.message}</p>}
                    {r.quantity && <p className="text-xs mt-1" style={{ color: "#4A6B82" }}>Qty: {r.quantity}</p>}
                  </div>
                  <div className="flex items-center gap-1.5">
                    <a href={`tel:${r.mobile}`} title="Call" className="p-2 rounded-lg border" style={{ borderColor: "rgba(8,127,191,0.15)", color: "#087FBF" }} data-testid={`call-${r.id}`}><Phone className="w-4 h-4" /></a>
                    {r.email && <a href={`mailto:${r.email}`} title="Email" className="p-2 rounded-lg border" style={{ borderColor: "rgba(8,127,191,0.15)", color: "#16B8D4" }} data-testid={`email-${r.id}`}><Mail className="w-4 h-4" /></a>}
                    <a href={whatsappLink(`Hello ${r.customer_name}, thank you for your enquiry to BIOSYN regarding ${r.product_name || "our aquaculture products"}.`)} target="_blank" rel="noopener noreferrer" title="WhatsApp" className="p-2 rounded-lg border" style={{ borderColor: "rgba(37,211,102,0.25)", color: "#25D366" }} data-testid={`wa-${r.id}`}><MessageCircle className="w-4 h-4" /></a>
                  </div>
                </div>
                <div className="mt-3 pt-3 border-t flex items-center gap-4" style={{ borderColor: "rgba(8,127,191,0.1)" }}>
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-semibold" style={{ color: "#4A6B82" }}>Status:</span>
                    <Select value={r.status} onValueChange={(v) => changeStatus(r.id, v)}>
                      <SelectTrigger className="h-8 w-40 text-xs" data-testid={`status-select-${r.id}`}><SelectValue /></SelectTrigger>
                      <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s.replace("_", " ")}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <NotesDialog enquiry={r} onSave={saveNotes} />
                  <span className="text-xs" style={{ color: "#94a3b8" }}>Mobile: {r.mobile}</span>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {!loading && (page > 0 || rows.length === PAGE_SIZE) && (
        <div className="mt-6 flex items-center justify-center gap-3">
          <button
            onClick={() => setPage((p) => Math.max(0, p - 1))}
            disabled={page === 0}
            data-testid="enq-prev"
            className="btn-outline-blue !py-2 !px-5 text-sm disabled:opacity-40"
          >
            Prev
          </button>
          <span className="text-sm font-semibold" style={{ color: "#4A6B82" }}>Page {page + 1}</span>
          <button
            onClick={() => setPage((p) => p + 1)}
            disabled={rows.length < PAGE_SIZE}
            data-testid="enq-next"
            className="btn-outline-blue !py-2 !px-5 text-sm disabled:opacity-40"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
}
